/**
 * One consignment leaving the factory under one document (ADR-037, Sprint 20).
 * Mirrors the deployed DDL field for field — `pms-database/PMSDatabase/tannery/Tables/
 * OutwardMovement*.sql`, `ToleranceDefault.sql`, `CustomerTolerance.sql`, `ExportProfile.sql`
 * are the authoritative shape; change this file only after those change. PascalCase because the
 * tannery API serialises with `PropertyNamingPolicy = null` (ADR-012), same as every other
 * tannery model in this library.
 *
 * Job work is deliberately NOT a purpose here — it has its own document in Sprints 22–23
 * (product owner, 2026-09-17). There is no gate-out screen and no `Tannery - Gate Out`
 * permission: a movement registers itself with the platform's own `dbo.GateInTable` at Approve
 * and gate-out is worked entirely from the common gate module (plan §11, decided 2026-09-18).
 */

import { AreaUnit } from './area-units';
import { PagedResult, TanneryListFilterBase } from './tannery-paging.models';

// ---------------------------------------------------------------------------
// Stored identifiers (mirror the CHECK constraints in the DDL above)
// ---------------------------------------------------------------------------

/** CK_OutwardMovement_Purpose. Decides which party fields the header needs (CK_OutwardMovement_Party) and whether it must name a sale order (CK_OutwardMovement_SaleOrder). */
export const OutwardMovementPurpose = {
  CustomerSale: 'CustomerSale',
  InterUnitTransfer: 'InterUnitTransfer',
  Sample: 'Sample',
  ReturnToCustomer: 'ReturnToCustomer',
} as const;
export type OutwardMovementPurposeValue = (typeof OutwardMovementPurpose)[keyof typeof OutwardMovementPurpose];
export const OUTWARD_MOVEMENT_PURPOSE_ALL: OutwardMovementPurposeValue[] = [
  OutwardMovementPurpose.CustomerSale,
  OutwardMovementPurpose.InterUnitTransfer,
  OutwardMovementPurpose.Sample,
  OutwardMovementPurpose.ReturnToCustomer,
];
export const OUTWARD_MOVEMENT_PURPOSE_LABELS: Record<string, string> = {
  [OutwardMovementPurpose.CustomerSale]: 'Customer sale',
  [OutwardMovementPurpose.InterUnitTransfer]: 'Inter-unit transfer',
  [OutwardMovementPurpose.Sample]: 'Sample',
  [OutwardMovementPurpose.ReturnToCustomer]: 'Return to customer',
};
export function outwardMovementPurposeLabel(purpose: string): string {
  return OUTWARD_MOVEMENT_PURPOSE_LABELS[purpose] ?? purpose;
}

/** CK_OutwardMovement_MaterialState. A header attribute, not a line one — one consignment carries one state (same vocabulary as `tannery.PackageMeasurement.Stage`, see `phase-exit-state.ts`). */
export const OutwardMaterialState = {
  WetBlue: 'WetBlue',
  Crust: 'Crust',
  Finished: 'Finished',
  ByProduct: 'ByProduct',
} as const;
export type OutwardMaterialStateValue = (typeof OutwardMaterialState)[keyof typeof OutwardMaterialState];
export const OUTWARD_MATERIAL_STATE_ALL: OutwardMaterialStateValue[] = [
  OutwardMaterialState.WetBlue,
  OutwardMaterialState.Crust,
  OutwardMaterialState.Finished,
  OutwardMaterialState.ByProduct,
];
export const OUTWARD_MATERIAL_STATE_LABELS: Record<string, string> = {
  [OutwardMaterialState.WetBlue]: 'Wet blue',
  [OutwardMaterialState.Crust]: 'Crust',
  [OutwardMaterialState.Finished]: 'Finished',
  [OutwardMaterialState.ByProduct]: 'By-product',
};
export function outwardMaterialStateLabel(state: string): string {
  return OUTWARD_MATERIAL_STATE_LABELS[state] ?? state;
}

/** CK_OutwardMovement_Status. CK_OutwardMovement_Stamps ties each status past Draft to the timestamp columns below. */
export const OutwardMovementStatus = {
  Draft: 'Draft',
  Submitted: 'Submitted',
  Approved: 'Approved',
  GateOut: 'GateOut',
  Cancelled: 'Cancelled',
} as const;
export type OutwardMovementStatusValue = (typeof OutwardMovementStatus)[keyof typeof OutwardMovementStatus];
export const OUTWARD_MOVEMENT_STATUS_ALL: OutwardMovementStatusValue[] = [
  OutwardMovementStatus.Draft,
  OutwardMovementStatus.Submitted,
  OutwardMovementStatus.Approved,
  OutwardMovementStatus.GateOut,
  OutwardMovementStatus.Cancelled,
];
export const OUTWARD_MOVEMENT_STATUS_LABELS: Record<string, string> = {
  [OutwardMovementStatus.Draft]: 'Draft',
  [OutwardMovementStatus.Submitted]: 'Submitted',
  [OutwardMovementStatus.Approved]: 'Approved',
  [OutwardMovementStatus.GateOut]: 'Gate out',
  [OutwardMovementStatus.Cancelled]: 'Cancelled',
};
export function outwardMovementStatusLabel(status: string): string {
  return OUTWARD_MOVEMENT_STATUS_LABELS[status] ?? status;
}
/** The one `nz-tag` colour mapping every screen that shows a movement status uses — no component may define a second one (kanzenflow-tannery-ui skill pattern 4). */
export function outwardMovementStatusColor(status: string): string {
  switch (status) {
    case OutwardMovementStatus.Draft:
      return 'default';
    case OutwardMovementStatus.Submitted:
      return 'processing';
    case OutwardMovementStatus.Approved:
      return 'gold';
    case OutwardMovementStatus.GateOut:
      return 'green';
    case OutwardMovementStatus.Cancelled:
      return 'red';
    default:
      return 'default';
  }
}

/** CK_OutwardMovementStatusHistory_Action — the verbs POST outward-movements/{id}/actions will accept (plan Phase B5/B8). */
export const OutwardMovementAction = {
  Create: 'Create',
  Submit: 'Submit',
  Approve: 'Approve',
  Reject: 'Reject',
  GateOut: 'GateOut',
  Cancel: 'Cancel',
} as const;
export type OutwardMovementActionValue = (typeof OutwardMovementAction)[keyof typeof OutwardMovementAction];
export const OUTWARD_MOVEMENT_ACTION_ALL: OutwardMovementActionValue[] = [
  OutwardMovementAction.Create,
  OutwardMovementAction.Submit,
  OutwardMovementAction.Approve,
  OutwardMovementAction.Reject,
  OutwardMovementAction.GateOut,
  OutwardMovementAction.Cancel,
];

/** CK_OutwardMovementStockEffect_EffectType — what one row of `tannery.OutwardMovementStockEffect` recorded against a label/rack. */
export const OutwardMovementEffectType = {
  Reserve: 'Reserve',
  Deduct: 'Deduct',
  Release: 'Release',
  Restore: 'Restore',
} as const;
export type OutwardMovementEffectTypeValue = (typeof OutwardMovementEffectType)[keyof typeof OutwardMovementEffectType];
export const OUTWARD_MOVEMENT_EFFECT_TYPE_ALL: OutwardMovementEffectTypeValue[] = [
  OutwardMovementEffectType.Reserve,
  OutwardMovementEffectType.Deduct,
  OutwardMovementEffectType.Release,
  OutwardMovementEffectType.Restore,
];

/** CK_ToleranceDefault_Measure / CK_CustomerTolerance_Measure. GradeMix has no verdict rule until Sprint 21 (`SaleOrderFulfilment` rule 3/4) — the value ships now because the table does. */
export const ToleranceMeasure = {
  Quantity: 'Quantity',
  Measurement: 'Measurement',
  GradeMix: 'GradeMix',
} as const;
export type ToleranceMeasureValue = (typeof ToleranceMeasure)[keyof typeof ToleranceMeasure];
export const TOLERANCE_MEASURE_ALL: ToleranceMeasureValue[] = [ToleranceMeasure.Quantity, ToleranceMeasure.Measurement, ToleranceMeasure.GradeMix];

/** CK_OutwardMovementExport_Endorsement — under GST an export leaves either on payment of IGST or under a LUT/bond; the invoice wording differs and is not interchangeable. */
export const ExportEndorsementType = {
  WithIgstPayment: 'WithIgstPayment',
  WithoutIgstPayment: 'WithoutIgstPayment',
} as const;
export type ExportEndorsementTypeValue = (typeof ExportEndorsementType)[keyof typeof ExportEndorsementType];

// ---------------------------------------------------------------------------
// View models (mirror the tables field for field)
// ---------------------------------------------------------------------------

/**
 * `tannery.OutwardMovement` — the header. The counterparty (`Counterparty*`) is snapshotted at
 * Approve so a challan printed in April still reads as it did if the customer master is edited
 * in June; `GateInId` is written at the same step, once the movement registers itself with the
 * platform's gate register. `TotalArea`/`AreaUnit` are null until every line agrees on one unit
 * (C1 — a movement mixing Sqft and Dm2 packages is refused at Submit).
 */
export interface OutwardMovement {
  Id: number;
  MovementNumber: string;
  FinancialYear: string;
  SequenceNumber: number;
  Purpose: OutwardMovementPurposeValue;
  MaterialState: OutwardMaterialStateValue;
  Status: OutwardMovementStatusValue;
  MovementDate: string;
  CustomerId: number | null;
  SaleOrderId: number | null;
  IsExport: boolean;
  CounterpartyName: string | null;
  CounterpartyAddress: string | null;
  CounterpartyGstin: string | null;
  CounterpartyState: string | null;
  TransportCompanyId: number | null;
  VehicleId: number | null;
  DriverName: string | null;
  DriverContact: string | null;
  LrNumber: string | null;
  LrDate: string | null;
  EWayBillNumber: string | null;
  EWayBillDate: string | null;
  GrossWeightKg: number | null;
  TareWeightKg: number | null;
  NetWeightKg: number | null;
  TotalPackages: number | null;
  TotalPieces: number | null;
  TotalWeightKg: number | null;
  TotalArea: number | null;
  AreaUnit: AreaUnit | null;
  GateInId: number | null;
  Remarks: string | null;
  SubmittedAt: string | null;
  SubmittedById: number | null;
  ApprovedAt: string | null;
  ApprovedById: number | null;
  GateOutAt: string | null;
  GateOutById: number | null;
  CancelledAt: string | null;
  CancelledById: number | null;
  CancelReason: string | null;
  CreatedAt?: string;
  CreatedBy?: string;
  UpdatedAt?: string;
  UpdatedBy?: string;
}

/**
 * `tannery.OutwardMovementLine` — one package on the consignment. A package is a label
 * (ADR-037): the line names one `dbo.StockLabelTable` row and the `tannery.PackageMeasurement`
 * row that describes it. Every measure here is a snapshot taken server-side when the line is
 * added — never posted by the client, and never re-read for a print. Partial dispatch is
 * refused; the operator splits the package by its pieces first (`POST outputs/package-splits`,
 * reached from the package picker and Leather stock) and dispatches either half — each has its
 * own measurement.
 */
export interface OutwardMovementLine {
  Id: number;
  OutwardMovementId: number;
  StockLabelId: number;
  PackageMeasurementId: number;
  SaleOrderLineId: number | null;
  PackageNumber: string;
  PackageType: string | null;
  /** FK_OutwardMovementLine_GradeMaster → `tannery.GradeMaster(Code)`, not a fixed vocabulary (C2). */
  Grade: string | null;
  /** Exact — no tolerance ever applies to a piece count (`SaleOrderFulfilment.PiecesExactRefusal`). */
  Pieces: number | null;
  WeightKg: number | null;
  Area: number | null;
  AreaUnit: AreaUnit | null;
  GrossWeightKg: number | null;
  StockQuantity: number;
  /** Snapshotted at Approve from `dbo.ProductMaster.HsnCode`, the platform's authoritative value. */
  HsnCode: string | null;
  Remarks: string | null;
  CreatedAt?: string;
  CreatedBy?: string;
}

/**
 * `tannery.OutwardMovementStockEffect` — one row per (line, effect), the idempotency guarantee
 * (`UX_OutwardMovementStockEffect_Line_Effect`) that stops a retried Approve or a double-clicked
 * Cancel from applying twice. `RackId` is the rack the quantity was taken FROM; Release and
 * Restore credit this rack, never the label's current one (ADR-037 — the platform helper
 * `StockReserveDataFn.ReleasePartialReservation` does the latter and is not called here).
 */
export interface OutwardMovementStockEffect {
  Id: number;
  OutwardMovementId: number;
  OutwardMovementLineId: number;
  EffectType: OutwardMovementEffectTypeValue;
  StockLabelId: number;
  StockProductId: number;
  RackId: number;
  Quantity: number;
  StockReserveId: number | null;
  AppliedAt: string;
  AppliedBy?: string;
}

/** `tannery.OutwardMovementStatusHistory` — appended, never edited, the same shape as `tannery.SaleOrderStatusHistory`. */
export interface OutwardMovementStatusHistory {
  Id: number;
  OutwardMovementId: number;
  FromStatus: OutwardMovementStatusValue | null;
  ToStatus: OutwardMovementStatusValue;
  Action: OutwardMovementActionValue;
  Remarks: string | null;
  ChangedAt: string;
  ChangedBy?: string;
}

/**
 * `tannery.OutwardMovementExport` — the export block, one row per exporting movement. Capture
 * and print only (ADR-037): no ICEGATE, no eCoO, no IRN. `LutNumber`/`LutDate` are copied from
 * `ExportProfile` at Approve, not joined at print time, for the same reason the counterparty is
 * snapshotted. Ships in Sprint 20; until the screen exists (Sprint 21) Submit refuses a movement
 * with `IsExport = true`.
 */
export interface OutwardMovementExport {
  Id: number;
  OutwardMovementId: number;
  ExportProfileId: number | null;
  ConsigneeName: string | null;
  ConsigneeAddress: string | null;
  ConsigneeCountry: string | null;
  NotifyParty: string | null;
  PortOfLoading: string | null;
  PortOfDischarge: string | null;
  FinalDestination: string | null;
  Incoterm: string | null;
  CurrencyCode: string | null;
  ExchangeRate: number | null;
  EndorsementType: ExportEndorsementTypeValue | null;
  LutNumber: string | null;
  LutDate: string | null;
  ContainerNumber: string | null;
  SealNumber: string | null;
  ShippingBillNumber: string | null;
  ShippingBillDate: string | null;
  BillOfLadingNumber: string | null;
  BillOfLadingDate: string | null;
  CertificateOfOriginNumber: string | null;
  CertificateOfOriginDate: string | null;
  /**
   * GST Rule 55(5): several delivery challans may travel against one tax invoice raised later.
   * The number is the batch key — `CarriesOriginalInvoice` says this consignment is the one
   * carrying the original, and the server refuses a second holder for the same number
   * (`CheckOriginalInvoiceHolder`). No date column exists: the invoice's own date lives with the
   * invoice, in accounts.
   */
  TaxInvoiceNumber: string | null;
  CarriesOriginalInvoice: boolean;
  Remarks: string | null;
  CreatedAt?: string;
  CreatedBy?: string;
  UpdatedAt?: string;
  UpdatedBy?: string;
}

/** `tannery.ExportProfile` — the tenant's export identity, per financial year. Its own master screen is Sprint 21; it ships now so `OutwardMovementExport` can reference it. */
export interface ExportProfile {
  Id: number;
  FinancialYear: string;
  IecNumber: string | null;
  LutNumber: string | null;
  LutDate: string | null;
  AdCode: string | null;
  BankName: string | null;
  BankAccountNumber: string | null;
  Remarks: string | null;
  CreatedAt?: string;
  CreatedBy?: string;
  UpdatedAt?: string;
  UpdatedBy?: string;
}

/**
 * `tannery.ToleranceDefault` — the tenant's own tolerance floor, nine seeded rows (one per
 * material state per measure). Tolerance resolves through four levels (ADR-037): the sale order
 * line, then the customer (`CustomerTolerance`), then the article's own softness
 * (`Article.IsSoftLeather`), then this table.
 */
export interface ToleranceDefault {
  Id: number;
  MaterialState: OutwardMaterialStateValue;
  Measure: ToleranceMeasureValue;
  TolerancePct: number;
  Remarks: string | null;
  CreatedAt?: string;
  CreatedBy?: string;
  UpdatedAt?: string;
  UpdatedBy?: string;
}

/**
 * `tannery.CustomerTolerance` — a customer's negotiated override. A row exists only where the
 * customer actually agreed something different; absence means "use the level below", never a
 * tolerance of 0%.
 */
export interface CustomerTolerance {
  Id: number;
  CustomerId: number;
  MaterialState: OutwardMaterialStateValue;
  Measure: ToleranceMeasureValue;
  TolerancePct: number;
  Remarks: string | null;
  CreatedAt?: string;
  CreatedBy?: string;
  UpdatedAt?: string;
  UpdatedBy?: string;
}

// ---------------------------------------------------------------------------
// List envelope
// ---------------------------------------------------------------------------

/** GET outward-movements' filter — narrows to status, purpose, material state, customer or sale order; send only what is set. Mirrors `TanneryListFilterBase` the way `BatchListFilter` does. */
export interface OutwardMovementListFilter extends TanneryListFilterBase {
  status?: OutwardMovementStatusValue;
  /** Zero or more additional statuses, unioned with status — mirrors `TanneryListPaging.SplitStatuses`. */
  statuses?: OutwardMovementStatusValue[];
  purpose?: OutwardMovementPurposeValue;
  materialState?: OutwardMaterialStateValue;
  customerId?: number;
  saleOrderId?: number;
  /** Inclusive lower bound against MovementDate. */
  dateFrom?: string;
  /** Inclusive upper bound against MovementDate. */
  dateTo?: string;
}

